import React, { Fragment, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Divider,
  Skeleton,
  Typography,
  Box,
  ListItemButton,
  ListItem,
  List
} from '@mui/material'
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { ApolloError } from '@apollo/client'

import { QueryError, MuiLink } from 'components'
import { BaseUserApplicationFragmentFragment } from 'api/generated'
import { useThemeType } from 'context/theme'

export type UserApplicationsListProps = {
  userApplications?: BaseUserApplicationFragmentFragment[]
  loading?: boolean
  error?: ApolloError
  refetch?(): void
}

const UserApplicationsList: React.FC<UserApplicationsListProps> = ({
  userApplications,
  loading,
  error,
  refetch
}: UserApplicationsListProps) => {
  const { t } = useTranslation()
  const { dateFnsLocale } = useThemeType()

  const handleRefetchQuery = useCallback(() => {
    refetch?.()
  }, [refetch])

  return (
    <>
      {error ? (
        <QueryError
          error={error}
          loading={loading}
          refetch={handleRefetchQuery}
        />
      ) : loading ? (
        <Skeleton variant='rectangular' width='100%' height='40vh' />
      ) : userApplications?.length ? (
        <List sx={{ height: '40vh', overflow: 'auto', width: '100%' }}>
          {userApplications?.map(userApplication => {
            return (
              <Fragment key={userApplication?.id}>
                <ListItem secondaryAction={<ChevronRightIcon />} disablePadding>
                  <ListItemButton
                    component={MuiLink}
                    to={`/user-applications/${userApplication?.id}`}
                  >
                    <Box flex={1} display='flex' flexDirection='column'>
                      <Typography variant='subtitle2'>
                        {userApplication?.apartment?.name}
                      </Typography>

                      <Typography variant='caption'>
                        {`${t(userApplication?.rental_type || '')} / ${t(
                          userApplication?.status || ''
                        )}`}
                      </Typography>
                    </Box>

                    <Box
                      display='flex'
                      flexDirection='column'
                      alignItems='flex-end'
                    >
                      <Typography variant='subtitle2'>
                        {`${userApplication?.first_name || ''} ${
                          userApplication?.last_name || ''
                        }`}
                      </Typography>

                      <Typography variant='caption'>
                        {formatDistanceToNow(
                          new Date(userApplication?.created_at),
                          {
                            addSuffix: true,
                            locale: dateFnsLocale
                          }
                        )}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Divider component='li' />
              </Fragment>
            )
          })}
        </List>
      ) : (
        <Box
          height='40vh'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <Typography>{t('noRowsLabel')}</Typography>
        </Box>
      )}
    </>
  )
}

export default UserApplicationsList
